import React, { useState, useEffect } from "react";
import { trackClick } from '../lib/analytics';

// Hysteresis around the shadow threshold: the bar picks up its shadow once the
// body has actually moved, and only drops it again back at the very top, so a
// one-pixel rubber-band scroll can't strobe it on and off.
const SHADOW_ON_AT = 8;
const SHADOW_OFF_AT = 2;

const SECTIONS = ["home", "we-got-you", "trusted-by", "faq"];

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [isScrolled, setIsScrolled] = useState(false);

  const toggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    let frame = null;

    const measure = () => {
      frame = null;
      const y = window.scrollY;

      setIsScrolled((wasScrolled) =>
        wasScrolled ? y > SHADOW_OFF_AT : y > SHADOW_ON_AT
      );

      // Scroll spy for active section
      const scrollPosition = y + 100;

      for (let section of SECTIONS) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    const handleScroll = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(measure);
      }
    };

    // Run once on mount so a restored or deep-linked scroll position starts
    // with the right shadow instead of waiting for the first scroll event.
    measure();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  const navItems = [
    { href: "#home", label: "Company", id: "home" },
    { href: "#we-got-you", label: "Services", id: "we-got-you" },
    { href: "#trusted-by", label: "About", id: "trusted-by" },
    { href: "#faq", label: "FAQ", id: "faq" },
  ];

  const handleNavClick = () => {
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      {/* Only the shadow transitions. Nothing here changes the bar's box, so
          the toolbar keeps the same height whether the page is at the top,
          scrolled, or has the mobile menu open. */}
      <nav
        className={`relative bg-white/95 backdrop-blur-md transition-shadow duration-300 dark:bg-gray-900/95 ${
          isScrolled ? "shadow-lg" : "shadow-none"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-screen-xl items-center justify-between px-4 lg:h-20 lg:px-6">
          <a
            href="#home"
            className="flex shrink-0 items-center group"
            aria-label="SDS Deliver IO Home"
          >
            <img
              src="/favicon.png"
              className="mr-3 h-8 sm:h-10 transition-transform group-hover:scale-110"
              alt="SDS Deliver Logo"
              loading="eager"
            />
            <span className="self-center whitespace-nowrap text-xl font-bold bg-gradient-to-r from-accent-500 to-accent-700 bg-clip-text text-transparent dark:from-accent-300 dark:to-accent-500">
              SDS Deliver IO
            </span>
          </a>

          <div className="flex shrink-0 items-center lg:order-2 space-x-2">
            <a
              href="#contact"
              onClick={() => trackClick('Get a Quote', 'header')}
              className="hidden sm:inline-block rounded-lg bg-gradient-to-r from-accent-500 to-accent-700 px-5 py-2.5 text-white font-semibold hover:from-accent-600 hover:to-accent-800 transition-colors duration-300 shadow-md hover:shadow-lg"
              aria-label="Get a quote for IT services"
            >
              Get a Quote
            </a>

            <button
              type="button"
              className="inline-flex items-center rounded-lg p-2 text-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-accent-600 lg:hidden transition-colors"
              aria-controls="mobile-menu-2"
              aria-expanded={isMenuOpen}
              aria-label="Toggle navigation menu"
              onClick={toggle}
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          </div>

          {/* On mobile this drops out of flow as a panel below the bar, so
              opening it can never grow the toolbar. On lg it sits inline. */}
          <div
            className={`${
              isMenuOpen ? "block" : "hidden"
            } absolute inset-x-0 top-full border-t border-gray-100 bg-white px-4 pb-4 shadow-lg dark:border-gray-800 dark:bg-gray-900 lg:static lg:order-1 lg:block lg:w-auto lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none dark:lg:bg-transparent`}
            id="mobile-menu-2"
          >
            <ul className="mt-4 flex flex-col font-medium lg:mt-0 lg:flex-row lg:space-x-8 bg-gray-50 lg:bg-transparent dark:bg-gray-800 lg:dark:bg-transparent rounded-lg lg:rounded-none p-4 lg:p-0">
              {navItems.map((item) => (
                <li key={item.id}>
                  <a
                    href={item.href}
                    onClick={handleNavClick}
                    className={`block py-2 px-3 rounded-lg transition-colors duration-300 ${
                      activeSection === item.id
                        ? "bg-accent-600 text-white lg:bg-transparent lg:text-accent-700 dark:lg:text-accent-400 font-semibold"
                        : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 lg:hover:bg-transparent lg:hover:text-accent-600 dark:lg:hover:text-accent-400"
                    }`}
                    aria-current={activeSection === item.id ? "page" : undefined}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>

            {/* Mobile CTA */}
            <a
              href="#contact"
              onClick={() => trackClick('Get a Quote', 'header_mobile')}
              className="sm:hidden mt-4 block rounded-lg bg-gradient-to-r from-accent-500 to-accent-700 px-5 py-2.5 text-center text-white font-semibold hover:from-accent-600 hover:to-accent-800 transition-colors duration-300"
              aria-label="Get a quote for IT services"
            >
              Get a Quote
            </a>
          </div>
        </div>
      </nav>
    </header>
  );
}

export default Header;
